import React, { useEffect, useState } from "react";
import './HotkeyEdit.css';
import { getHotkeyStore } from '../../store/hotkeyStore';
import DraggableModal from '../Modal/DraggableModal';

function HotkeyEdit({ displayModal, actions, setActions, characterId, initialSet = 0 }) {
    const keys = ['F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9'];
    const mouseKeys = ['M1', 'M2', 'M3', 'M4', 'M5'];
    const [inputValues, setInputValues] = useState({});
    const [mouseInputValues, setMouseInputValues] = useState({});
    const [ctrlClickValues, setCtrlClickValues] = useState({
        leftClick: { name: "", cmd: "" },
        rightClick: { name: "", cmd: "" }
    });
    const [actionSet, setActionSet] = useState(initialSet);
    const [hasChanges, setHasChanges] = useState(false);
    const [mouseOptions, setMouseOptions] = useState([]);
    const [ctrlClickOptions, setCtrlClickOptions] = useState({
        leftClick: { name: "Attack", cmd: "/a {target}" },
        rightClick: { name: "Examine", cmd: "/examine {target}" }
    });

    // Ensure we're using the persisted actions and mouse options from the store
    useEffect(() => {
        // If we have a character ID but no actions loaded yet, try to get them from the store
        if (characterId) {
            const store = getHotkeyStore(characterId);
            if (store) {
                // Get actions
                if (!actions || actions.length === 0) {
                    const storedActions = store.getState().actions;
                    if (storedActions && storedActions.length > 0) {
                        setActions(storedActions);
                    }
                }
                
                // Get mouse options
                const storedMouseOptions = store.getState().mouseOptions;
                if (storedMouseOptions && storedMouseOptions.length > 0) {
                    setMouseOptions(storedMouseOptions);
                }
                
                // Get ctrl+click options
                const storedCtrlClickOptions = store.getState().ctrlClickOptions;
                if (storedCtrlClickOptions) {
                    setCtrlClickOptions(storedCtrlClickOptions);
                    setCtrlClickValues(storedCtrlClickOptions);
                }
            }
        }
    }, [characterId, actions, setActions]);

    function handleInputChange(key, name, cmd) {
        setInputValues((prevInputValues) => ({
            ...prevInputValues,
            [key]: { name, cmd },
        }));
        setHasChanges(true);
    }

    function handleMouseInputChange(key, name, cmd) {
        setMouseInputValues((prevInputValues) => ({
            ...prevInputValues,
            [key]: { name, cmd },
        }));
        setHasChanges(true);
    }
    
    function handleCtrlClickChange(clickType, name, cmd) {
        setCtrlClickValues((prev) => ({
            ...prev,
            [clickType]: { name, cmd }
        }));
        setHasChanges(true);
    }

    function handleSaveClick() {
        // Save function key actions
        if (actionSet >= 0 && actions && actions.length > 0) {
            const updatedActions = JSON.parse(JSON.stringify(actions)); // Deep copy
            
            keys.forEach((key) => {
                if (inputValues[key]) {
                    const { name, cmd } = inputValues[key];
                    const functionIndex = updatedActions[actionSet].findIndex(
                        (action) => action.key === key
                    );
                    if (functionIndex !== -1) {
                        updatedActions[actionSet][functionIndex] = {
                            ...updatedActions[actionSet][functionIndex],
                            name,
                            cmd,
                        };
                    }
                }
            });
            
            setActions(updatedActions);
            
            // Save to localStorage through the store if we have a character ID
            if (characterId) {
                const store = getHotkeyStore(characterId);
                if (store) {
                    store.getState().setActions(updatedActions);
                }
            }
        }
        
        // Save mouse options and ctrl+click options
        if (actionSet === -1 && mouseOptions && mouseOptions.length > 0) {
            // Save mouse options
            const updatedMouseOptions = JSON.parse(JSON.stringify(mouseOptions)); // Deep copy
            
            mouseKeys.forEach((key) => {
                if (mouseInputValues[key]) {
                    const { name, cmd } = mouseInputValues[key];
                    const mouseIndex = updatedMouseOptions.findIndex(
                        (option) => option.key === key
                    );
                    if (mouseIndex !== -1) {
                        updatedMouseOptions[mouseIndex] = {
                            ...updatedMouseOptions[mouseIndex],
                            name,
                            cmd,
                        };
                    }
                }
            });
            
            setMouseOptions(updatedMouseOptions);
            
            // Save ctrl+click options
            const updatedCtrlClickOptions = {
                leftClick: ctrlClickValues.leftClick,
                rightClick: ctrlClickValues.rightClick
            };
            
            setCtrlClickOptions(updatedCtrlClickOptions);
            
            // Save to localStorage through the store if we have a character ID
            if (characterId) {
                const store = getHotkeyStore(characterId);
                if (store) {
                    store.getState().setMouseOptions(updatedMouseOptions);
                    store.getState().setCtrlClickOptions(updatedCtrlClickOptions);
                }
            }
        }
        
        setHasChanges(false);
    }

    function changeSet(val) {
        if (val >= -2 && val <= actions?.length - 1) {
            setActionSet(val);
            setInputValues({}); // Clear input values when changing sets
            setMouseInputValues({}); // Clear mouse input values when changing sets
        }
    }

    // If no actions are available yet, show a loading state
    if ((!actions || actions.length === 0) && (!mouseOptions || mouseOptions.length === 0)) {
        return (
            <DraggableModal modalId="hotkey" onClose={displayModal}>
                <div className="modal-content" style={{ width: '600px' }}>
                    <div className="tmodal-header">
                        <span className="close" onClick={displayModal}>&times;</span>
                        <h2>Edit Hotkey</h2>
                    </div>
                    <div className="modal-body" style={{ width: '100%', padding: '2px 5px' }}>
                        <p>Loading hotkeys...</p>
                    </div>
                    <div className="modal-footer">
                        <button className="ts-close" onClick={displayModal}>Close</button>
                    </div>
                </div>
            </DraggableModal>
        );
    }

    return (
        <DraggableModal modalId="hotkey" onClose={displayModal}>
            <div className="modal-content" style={{ width: '600px' }}>
                <div className="tmodal-header">
                    <span className="close" onClick={displayModal}>&times;</span>
                    <h2>Edit Hotkey</h2>
                </div>
                <div className="modal-body" style={{ width: '100%', padding: '2px 5px' }}>
                    <HotkeyOptions changeSet={changeSet} actionSet={actionSet} />
                    <div className="edit-wrapper">
                        {actionSet === -2 ? (
                            <div className="help-section-container">
                                <div className="help-section">
                                    <h3>Hotkey Guide</h3>
                                    <p>
                                        Hotkeys allow you to set custom actions like attack or cast a spell. 
                                        You can find the action bar on the bottom left of the client. 
                                        Simply click on the action button, or press F1-F9 on the keyboard to perform the action.
                                    </p>
                                    
                                    <h4>Hotkey Sets</h4>
                                    <p>
                                        There are 4 custom sets that you can switch between. To switch sets:
                                        <br />• Click the number below the action bar
                                        <br />• Hold Shift + Left/Right arrow keys
                                    </p>
                                    
                                    <h3>Command Reference</h3>
                                    
                                    <div className="command-category">
                                        <h4>Combat Commands</h4>
                                        <table className="hk-table">
                                            <thead>
                                                <tr>
                                                    <th>Command</th>
                                                    <th>Description</th>
                                                    <th>Example</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>/a</td>
                                                    <td>Attack selected target</td>
                                                    <td></td>
                                                </tr>
                                                <tr>
                                                    <td>/a target</td>
                                                    <td>Attack a specific target</td>
                                                    <td>/a brawler</td>
                                                </tr>
                                                <tr>
                                                    <td>/stop</td>
                                                    <td>Stop attacking/casting</td>
                                                    <td></td>
                                                </tr>
                                                <tr>
                                                    <td>/s ability</td>
                                                    <td>Use an ability</td>
                                                    <td>/s kick</td>
                                                </tr>
                                                <tr>
                                                    <td>/s ability, target</td>
                                                    <td>Use ability on target</td>
                                                    <td>/s fireball, 0</td>
                                                </tr>
                                                <tr>
                                                    <td>/autoattack</td>
                                                    <td>Toggle auto attack</td>
                                                    <td></td>
                                                </tr>
                                                <tr>
                                                    <td>/ac spell, target</td>
                                                    <td>Autocast a spell</td>
                                                    <td>/ac fireball, 0</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    
                                    <div className="command-category">
                                        <h4>Communication Commands</h4>
                                        <table className="hk-table">
                                            <thead>
                                                <tr>
                                                    <th>Command</th>
                                                    <th>Description</th>
                                                    <th>Example</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>/g message</td>
                                                    <td>Global chat</td>
                                                    <td>/g hello everyone!</td>
                                                </tr>
                                                <tr>
                                                    <td>/w name message</td>
                                                    <td>Whisper to player</td>
                                                    <td>/w maven hi there</td>
                                                </tr>
                                                <tr>
                                                    <td>/r message</td>
                                                    <td>Reply to last whisper</td>
                                                    <td>/r thanks!</td>
                                                </tr>
                                                <tr>
                                                    <td>/p message</td>
                                                    <td>Party chat</td>
                                                    <td>/p need healing!</td>
                                                </tr>
                                                <tr>
                                                    <td>/e emote</td>
                                                    <td>Emote action</td>
                                                    <td>/e waves hello</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    
                                    <div className="command-category">
                                        <h4>Utility Commands</h4>
                                        <table className="hk-table">
                                            <thead>
                                                <tr>
                                                    <th>Command</th>
                                                    <th>Description</th>
                                                    <th>Example</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>/examine target</td>
                                                    <td>Examine player/mob</td>
                                                    <td>/examine brawler</td>
                                                </tr>
                                                <tr>
                                                    <td>/use item</td>
                                                    <td>Use inventory item</td>
                                                    <td>/use health potion</td>
                                                </tr>
                                                <tr>
                                                    <td>/get</td>
                                                    <td>Pick up items</td>
                                                    <td></td>
                                                </tr>
                                                <tr>
                                                    <td>/stats</td>
                                                    <td>View character stats</td>
                                                    <td></td>
                                                </tr>
                                                <tr>
                                                    <td>/skills</td>
                                                    <td>View character skills</td>
                                                    <td></td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    
                                    <h3>Target Reference</h3>
                                    <p>
                                        When targeting, you can use:
                                        <br />• Full or partial name (e.g., "brawler" or "bra")
                                        <br />• Position in room list (0 is first mob/player)
                                        <br />• Press TAB to target yourself
                                    </p>
                                    
                                    <h3>Mouse Options</h3>
                                    <p>
                                        Mouse options (M1-M5) appear in the context menu when you right-click on a player or mob.
                                        Use {'{target}'} in your commands to insert the target's name automatically.
                                    </p>
                                    
                                    <h3>Ctrl+Click Options</h3>
                                    <p>
                                        Hold Ctrl while clicking on a player or mob to quickly perform actions:
                                        <br />• Ctrl+Left Click: Default is attack
                                        <br />• Ctrl+Right Click: Default is examine
                                    </p>
                                </div>
                            </div>
                        ) : actionSet === -1 ? (
                            <>
                                <h3>Mouse Options</h3>
                                <p className="mouse-options-help">
                                    These options will appear in the context menu when you right-click on a player or mob.
                                    Use {'{target}'} in your commands to insert the target's index or name automatically.
                                </p>
                                <HotKeyHeading />
                                {mouseKeys.map((key) => (
                                    <HotKeyInput
                                        key={key}
                                        k={key}
                                        action={mouseOptions.find((option) => option.key === key)}
                                        onUpdateAction={handleMouseInputChange}
                                    />
                                ))}
                                
                                <h3 className="ctrl-click-heading">Ctrl+Click Options</h3>
                                <p className="mouse-options-help">
                                    These actions are performed when holding Ctrl while clicking on a player or mob.
                                    Use {'{target}'} in your commands to insert the target's index or name automatically.
                                </p>
                                <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr', fontWeight: '600', paddingLeft: '2px', marginBottom: '2px' }}>
                                    <span>Action</span>
                                    <span>Name</span>
                                    <span>Command</span>
                                </div>
                                
                                {/* Ctrl + Left Click */}
                                <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr', gap: '4px', marginBottom: '8px' }}>
                                    <span>Ctrl + Left Click</span>
                                    <input
                                        className="hotkey-input"
                                        type="text"
                                        value={ctrlClickValues.leftClick.name}
                                        onChange={(e) => handleCtrlClickChange('leftClick', e.target.value, ctrlClickValues.leftClick.cmd)}
                                        onKeyDown={(e) => e.stopPropagation()}
                                    />
                                    <input
                                        className="hotkey-input"
                                        type="text"
                                        value={ctrlClickValues.leftClick.cmd}
                                        onChange={(e) => handleCtrlClickChange('leftClick', ctrlClickValues.leftClick.name, e.target.value)}
                                        onKeyDown={(e) => e.stopPropagation()}
                                    />
                                </div>
                                
                                {/* Ctrl + Right Click */}
                                <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr', gap: '4px' }}>
                                    <span>Ctrl + Right Click</span>
                                    <input
                                        className="hotkey-input"
                                        type="text"
                                        value={ctrlClickValues.rightClick.name}
                                        onChange={(e) => handleCtrlClickChange('rightClick', e.target.value, ctrlClickValues.rightClick.cmd)}
                                        onKeyDown={(e) => e.stopPropagation()}
                                    />
                                    <input
                                        className="hotkey-input"
                                        type="text"
                                        value={ctrlClickValues.rightClick.cmd}
                                        onChange={(e) => handleCtrlClickChange('rightClick', ctrlClickValues.rightClick.name, e.target.value)}
                                        onKeyDown={(e) => e.stopPropagation()}
                                    />
                                </div>
                            </>
                        ) : (
                            <>
                                <HotKeyHeading />
                                {keys.map((key) => (
                                    <HotKeyInput
                                        key={key}
                                        k={key}
                                        action={actions[actionSet].find((action) => action.key === key)}
                                        onUpdateAction={handleInputChange}
                                    />
                                ))}
                            </>
                        )}
                    </div>
                </div>
                <div className="modal-footer">
                    <button className="ts-close" onClick={displayModal}>Close</button>
                    <button 
                        className="ts-button" 
                        onClick={handleSaveClick}
                        disabled={!hasChanges}
                    >
                        Save
                    </button>
                </div>
            </div>
        </DraggableModal>
    );
}

function HotkeyOptions({ actionSet, changeSet }) {
    return (
        <div className="hotkey-sets">
            <button className={(actionSet === -2) ? "btn-active" : ""} onClick={() => changeSet(-2)}>Help</button>
            <button className={(actionSet === -1) ? "btn-active" : ""} onClick={() => changeSet(-1)}>Mouse</button>
            <button className={(actionSet === 0) ? "btn-active" : ""} onClick={() => changeSet(0)}>Set 1</button>
            <button className={(actionSet === 1) ? "btn-active" : ""} onClick={() => changeSet(1)}>Set 2</button>
            <button className={(actionSet === 2) ? "btn-active" : ""} onClick={() => changeSet(2)}>Set 3</button>
            <button className={(actionSet === 3) ? "btn-active" : ""} onClick={() => changeSet(3)}>Set 4</button>
        </div>
    )
}

function HotKeyHeading() {
    return (
        <div style={{ display: 'grid', gridTemplateColumns: '35px 1fr 1fr', fontWeight: '600', paddingLeft: '2px', marginBottom: '2px' }}>
            <span>Key</span>
            <span>Name</span>
            <span>Command</span>
        </div>
    )
}

function HotKeyInput({ k, action, onUpdateAction }) {
    const [hotkeyName, setHotkeyName] = useState(action?.name || "");
    const [hotkeyCmd, setHotkeyCmd] = useState(action?.cmd || "");

    useEffect(() => {
        setHotkeyName(action?.name || "");
        setHotkeyCmd(action?.cmd || "");
    }, [action]);

    const handleInputChange = () => {
        onUpdateAction(k, hotkeyName, hotkeyCmd);
    };

    return (
        <div style={{ display: 'grid', gridTemplateColumns: '35px 1fr 1fr', gap: '4px' }}>
            <span>{k}</span>
            <input
                className="hotkey-input"
                type="text"
                onChange={(e) => setHotkeyName(e.currentTarget.value)}
                onKeyDown={(e) => e.stopPropagation()}
                value={hotkeyName}
                onBlur={handleInputChange}
            />
            <input
                className="hotkey-input"
                type="text"
                onChange={(e) => setHotkeyCmd(e.currentTarget.value)}
                onKeyDown={(e) => e.stopPropagation()}
                value={hotkeyCmd}
                onBlur={handleInputChange}
            />
        </div>
    );
}

export default HotkeyEdit;